// TowerCraneUpdate/TowerCraneUpdatePage.js
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import api from "../Axios/axios";
import "./TowerCraneUpdatePage.scss";

const initialCrane = {
  id: 0,
  serialNo: "",
  ownerId: "",
  brandId: "",
  code: "",
  height: "",
  bom: "",
  maxTonnage: "",
  creationDate: "",
  type: "Chassis",
  hasConnectionEquipment: false,
  connectionEquipmentCount: "",
  isInWarehouse: false,
  warehouseId: "",
  currentSiteName: "",
  currentSiteAddress: "",
  hasWalkingGear: false,
  isManualRented: false,
  manualRentalCompany: "",
  manualRentalFee: "",
  manualRentalStartDate: "",
  manualRentalEndDate: "",
  isManualReserved: false,
  manualReserveCompany: "",
  manualReserveStartDate: "",
  manualReserveEndDate: "",
};

const parseCurrencyValue = (value) => {
  const normalized = String(value || "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsedValue = Number(normalized);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatCurrencyValue = (value) => {
  const parsedValue = parseCurrencyValue(value);
  if (!parsedValue) return "";

  return parsedValue.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  return String(dateStr).substring(0, 10);
};

export default function TowerCraneUpdatePage() {
  const [searchParams] = useSearchParams();
  const [updateMode, setUpdateMode] = useState("tower"); // "tower" | "warehouse"
  const [towerCranes, setTowerCranes] = useState([]);
  const [selectedCraneId, setSelectedCraneId] = useState("");
  const [formData, setFormData] = useState(initialCrane);

  const [brands, setBrands] = useState([]);
  const [craneOwners, setCraneOwners] = useState([]);
  const [depots, setDepots] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // 1) Kule vinçler
  useEffect(() => {
    const fetchCranes = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/TowerCrane");
        const list = res?.data?.data ?? [];
        setTowerCranes(list);
      } catch (err) {
        console.error(err);
        setError("Kule vinçler çekilirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    fetchCranes();
  }, []);

  // 2) Markalar, depolar, makine sahipleri (AddCrane ile aynı mantık)
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await api.get("/Brand");
        setBrands(response.data?.data ?? []);
      } catch (error) {
        console.error("Markalar yüklenemedi:", error);
      }
    };

    const fetchDepots = async () => {
      try {
        const response = await api.get("/Warehouse");
        setDepots(response.data?.data ?? []);
      } catch (error) {
        console.error("Depolar yüklenemedi:", error);
      }
    };

    const fetchCraneOwners = async () => {
      try {
        const response = await api.get("/Company");
        const allCompanies = response.data?.data ?? [];
        const owners = allCompanies.filter((company) => company.hasTowerCrane);
        setCraneOwners(owners);
      } catch (error) {
        console.error("Kule vinç sahipleri yüklenemedi:", error);
      }
    };

    fetchBrands();
    fetchDepots();
    fetchCraneOwners();
  }, []);

  // Güncelleme tipi değişince formu temizle
  useEffect(() => {
    setSelectedCraneId("");
    setFormData(initialCrane);
  }, [updateMode]);

  useEffect(() => {
    const craneId = searchParams.get("craneId");
    if (craneId) {
      setUpdateMode("tower");
      setSelectedCraneId(craneId);
    }
  }, [searchParams]);

  // Seçilen vinç değişince formu doldur
  useEffect(() => {
    if (!selectedCraneId) {
      setFormData(initialCrane);
      return;
    }

    const cranesArray = Array.isArray(towerCranes) ? towerCranes : [];
    const selected = cranesArray.find(
      (c) => String(c.id) === String(selectedCraneId)
    );

    if (!selected) {
      setFormData(initialCrane);
      return;
    }

    setFormData({
      id: selected.id ?? 0,
      serialNo: selected.serialNo ?? "",
      ownerId: selected.ownerId ?? "",
      brandId: selected.brandId ?? "",
      code: selected.code ?? "",
      height: selected.height ?? "",
      bom: selected.bom ?? "",
      maxTonnage: selected.maxTonnage ?? "",
      creationDate: selected.creationDate ?? "",
      type: selected.type ?? "Chassis",
      hasConnectionEquipment: selected.hasConnectionEquipment ?? false,
      connectionEquipmentCount: selected.connectionEquipmentCount ?? "",
      isInWarehouse: selected.isInWarehouse ?? false,
      warehouseId: selected.warehouseId ?? "",
      currentSiteName: selected.currentSiteName ?? "",
      currentSiteAddress: selected.currentSiteAddress ?? "",
      hasWalkingGear: selected.hasWalkingGear ?? false,
      isManualRented: selected.isManualRented ?? false,
      manualRentalCompany: selected.manualRentalCompany ?? "",
      manualRentalFee: formatCurrencyValue(selected.manualRentalFee),
      manualRentalStartDate: formatDateForInput(selected.manualRentalStartDate),
      manualRentalEndDate: formatDateForInput(selected.manualRentalEndDate),
      isManualReserved: selected.isManualReserved ?? false,
      manualReserveCompany: selected.manualReserveCompany ?? "",
      manualReserveStartDate: formatDateForInput(selected.manualReserveStartDate),
      manualReserveEndDate: formatDateForInput(selected.manualReserveEndDate),
    });
  }, [selectedCraneId, towerCranes]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "isInWarehouse" && checked
        ? { currentSiteName: "", currentSiteAddress: "" }
        : {}),
      ...(name === "isManualRented" && !checked
        ? {
            manualRentalCompany: "",
            manualRentalFee: "",
            manualRentalStartDate: "",
            manualRentalEndDate: "",
          }
        : {}),
      ...(name === "isManualRented" && checked
        ? {
            isManualReserved: false,
            manualReserveCompany: "",
            manualReserveStartDate: "",
            manualReserveEndDate: "",
          }
        : {}),
      ...(name === "isManualReserved" && !checked
        ? {
            manualReserveCompany: "",
            manualReserveStartDate: "",
            manualReserveEndDate: "",
          }
        : {}),
      ...(name === "isManualReserved" && checked
        ? {
            isManualRented: false,
            manualRentalCompany: "",
            manualRentalFee: "",
            manualRentalStartDate: "",
            manualRentalEndDate: "",
          }
        : {}),
    }));
  };

  const handleManualRentalFeeChange = (e) => {
    const value = e.target.value.replace(/[^\d.,]/g, "");
    setFormData((prev) => ({
      ...prev,
      manualRentalFee: value,
    }));
  };

  const handleManualRentalFeeBlur = () => {
    setFormData((prev) => ({
      ...prev,
      manualRentalFee: formatCurrencyValue(prev.manualRentalFee),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCraneId) {
      alert("Lütfen bir kule vinç seçin.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Swagger’daki modele uygun payload
      const payload = {
        id: Number(formData.id || selectedCraneId),
        serialNo: formData.serialNo || "",
        ownerId: formData.ownerId || null,
        brandId: formData.brandId ? Number(formData.brandId) : null,
        code: formData.code || "",
        height:
          formData.height === "" || formData.height == null
            ? null
            : Number(formData.height),
        bom: formData.bom || "",
        maxTonnage:
          formData.maxTonnage === "" || formData.maxTonnage == null
            ? null
            : Number(formData.maxTonnage),
        creationDate: formData.creationDate, // backend’in gönderdiği ISO string
        type: formData.type || "Chassis",
        hasConnectionEquipment: !!formData.hasConnectionEquipment,
        connectionEquipmentCount:
          formData.connectionEquipmentCount === "" ||
          formData.connectionEquipmentCount == null
            ? null
            : Number(formData.connectionEquipmentCount),
        isInWarehouse: !!formData.isInWarehouse,
        warehouseId:
          formData.warehouseId === "" || formData.warehouseId == null
            ? null
            : Number(formData.warehouseId),
        currentSiteName: formData.isInWarehouse ? null : formData.currentSiteName || null,
        currentSiteAddress: formData.isInWarehouse ? null : formData.currentSiteAddress || null,
        hasWalkingGear:
          formData.hasWalkingGear === "" || formData.hasWalkingGear == null
            ? null
            : !!formData.hasWalkingGear,
        isManualRented: !!formData.isManualRented,
        manualRentalCompany: formData.isManualRented ? formData.manualRentalCompany || null : null,
        manualRentalFee: formData.isManualRented ? parseCurrencyValue(formData.manualRentalFee) : null,
        manualRentalStartDate: formData.isManualRented ? formData.manualRentalStartDate || null : null,
        manualRentalEndDate: formData.isManualRented ? formData.manualRentalEndDate || null : null,
        isManualReserved: !!formData.isManualReserved,
        manualReserveCompany: formData.isManualReserved ? formData.manualReserveCompany || null : null,
        manualReserveStartDate: formData.isManualReserved ? formData.manualReserveStartDate || null : null,
        manualReserveEndDate: formData.isManualReserved ? formData.manualReserveEndDate || null : null,
      };

      // Swagger ile aynı: PUT /api/TowerCrane
      await api.put("/TowerCrane", payload);

      alert("Kule vinç bilgileri başarıyla güncellendi.");
    } catch (err) {
      console.error(err);
      setError("Güncelleme sırasında bir hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const isWarehouseMode = updateMode === "warehouse";

  // Depo modunda sadece depo ile ilgili alanlar aktif olsun
  const isFieldDisabled = (fieldName) => {
    if (!isWarehouseMode) return false;
    // Depo modunda SADECE depo ve depoda mı checkbox'ı değişebilir
    return !["warehouseId", "isInWarehouse"].includes(fieldName);
  };

  const cranesArray = Array.isArray(towerCranes) ? towerCranes : [];

  // Depo modu → sadece status === 'Active' olan makineler
  const filteredCranes = isWarehouseMode
    ? cranesArray.filter((c) => c.status === "Active")
    : cranesArray;

  const formatCreationDateForInput = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.substring(0, 16); // ISO → yyyy-MM-ddTHH:mm
  };

  return (
    <>
      <Navbar />
      <div className="tower-crane-update-page">
        <div className="tower-crane-update-page__content">
          <h1 className="tower-crane-update-page__title">Kule Vinç Güncelleme</h1>

          {/* Üst satır: Güncelleme Tipi + Kule Vinç Seç */}
          <div className="tower-crane-update-page__top-row">
            <div className="tower-crane-update-page__field">
              <label className="tower-crane-update-page__label">
                Güncelleme Tipi
              </label>
              <select
                className="tower-crane-update-page__select"
                value={updateMode}
                onChange={(e) => setUpdateMode(e.target.value)}
              >
                <option value="tower">Kule Vinç (Tüm Bilgiler)</option>
                <option value="warehouse">Depo (Sadece Depo Bilgisi)</option>
              </select>
            </div>

            <div className="tower-crane-update-page__field">
              <label className="tower-crane-update-page__label">
                Kule Vinç Seç
              </label>
              <select
                className="tower-crane-update-page__select"
                value={selectedCraneId}
                onChange={(e) => setSelectedCraneId(e.target.value)}
                disabled={loading || filteredCranes.length === 0}
              >
                <option value="">Seçiniz</option>
                {filteredCranes.map((crane) => (
                  <option key={crane.id} value={crane.id}>
                    {crane.serialNo || crane.code || `#${crane.id}`}
                  </option>
                ))}
              </select>
              {loading && (
                <div className="tower-crane-update-page__info">Yükleniyor...</div>
              )}
              {!loading && filteredCranes.length === 0 && (
                <div className="tower-crane-update-page__info">
                  Gösterilecek kule vinç bulunamadı.
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="tower-crane-update-page__error">{error}</div>
          )}

          {/* Form */}
          <form
            className="tower-crane-update-page__form"
            onSubmit={handleSubmit}
          >
            <div className="tower-crane-update-page__grid">
              {/* Seri No */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Seri No</label>
                <input
                  type="text"
                  name="serialNo"
                  value={formData.serialNo}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("serialNo")}
                />
              </div>

              {/* Makine Sahibi (Owner) */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">
                  Makine Sahibi
                </label>
                <select
                  name="ownerId"
                  value={formData.ownerId}
                  onChange={handleChange}
                  className="tower-crane-update-page__select"
                  disabled={isFieldDisabled("ownerId")}
                >
                  <option value="">Makine Sahibi Seç</option>
                  {craneOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Marka */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Marka</label>
                <select
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                  className="tower-crane-update-page__select"
                  disabled={isFieldDisabled("brandId")}
                >
                  <option value="">Marka Seç</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Kod */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Kod</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("code")}
                />
              </div>

              {/* Yükseklik */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">
                  Yükseklik (m)
                </label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("height")}
                />
              </div>

              {/* Max Tonaj */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">
                  Max Tonaj (ton)
                </label>
                <input
                  type="number"
                  name="maxTonnage"
                  value={formData.maxTonnage}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("maxTonnage")}
                />
              </div>

              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Bom (m)</label>
                <input
                  type="number"
                  name="bom"
                  value={formData.bom}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("bom")}
                />
              </div>

              {/* Oluşturma Tarihi (otomatik, değişemez) */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">
                  Oluşturma Tarihi
                </label>
                <input
                  type="datetime-local"
                  name="creationDate"
                  value={formatCreationDateForInput(formData.creationDate)}
                  onChange={() => {}}
                  className="tower-crane-update-page__input"
                  disabled={true}
                />
              </div>

              {/* Tip */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Tip</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="tower-crane-update-page__select"
                  disabled={isFieldDisabled("type")}
                >
                  <option value="Chassis">Chassis</option>
                  <option value="Anchored">Anchored</option>
                  <option value="Walking">Walking</option>
                </select>
              </div>

              {/* Bağlantı ekipmanı */}
              <div className="tower-crane-update-page__field tower-crane-update-page__field--checkbox">
                <label className="tower-crane-update-page__checkbox-label">
                  <input
                    type="checkbox"
                    name="hasConnectionEquipment"
                    checked={formData.hasConnectionEquipment}
                    onChange={handleChange}
                    disabled={isFieldDisabled("hasConnectionEquipment")}
                  />
                  Bağlantı Ekipmanı Var
                </label>
              </div>

              {/* Bağlantı ekipmanı sayısı */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">
                  Bağlantı Ekipmanı Sayısı
                </label>
                <input
                  type="number"
                  name="connectionEquipmentCount"
                  value={formData.connectionEquipmentCount}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("connectionEquipmentCount")}
                />
              </div>

              {/* Depoda mı */}
              <div className="tower-crane-update-page__field tower-crane-update-page__field--checkbox">
                <label className="tower-crane-update-page__checkbox-label">
                  <input
                    type="checkbox"
                    name="isInWarehouse"
                    checked={formData.isInWarehouse}
                    onChange={handleChange}
                    disabled={isFieldDisabled("isInWarehouse")}
                  />
                  Depoda
                </label>
              </div>

              {/* Depo seçimi */}
              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Depo</label>
                <select
                  name="warehouseId"
                  value={formData.warehouseId}
                  onChange={handleChange}
                  className="tower-crane-update-page__select"
                  disabled={isFieldDisabled("warehouseId")}
                >
                  <option value="">Depo Seç</option>
                  {depots.map((depot) => (
                    <option key={depot.id} value={depot.id}>
                      {depot.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Şantiye Adı</label>
                <input
                  type="text"
                  name="currentSiteName"
                  value={formData.currentSiteName}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("currentSiteName") || formData.isInWarehouse}
                />
              </div>

              <div className="tower-crane-update-page__field">
                <label className="tower-crane-update-page__label">Şantiye Adresi</label>
                <input
                  type="text"
                  name="currentSiteAddress"
                  value={formData.currentSiteAddress}
                  onChange={handleChange}
                  className="tower-crane-update-page__input"
                  disabled={isFieldDisabled("currentSiteAddress") || formData.isInWarehouse}
                />
              </div>

              {/* Yürüyüş takımı */}
              <div className="tower-crane-update-page__field tower-crane-update-page__field--checkbox">
                <label className="tower-crane-update-page__checkbox-label">
                  <input
                    type="checkbox"
                    name="hasWalkingGear"
                    checked={formData.hasWalkingGear}
                    onChange={handleChange}
                    disabled={isFieldDisabled("hasWalkingGear")}
                  />
                  Yürüyüş Takımı Var
                </label>
              </div>

              <div className="tower-crane-update-page__field tower-crane-update-page__field--checkbox">
                <label className="tower-crane-update-page__checkbox-label">
                  <input
                    type="checkbox"
                    name="isManualRented"
                    checked={formData.isManualRented}
                    onChange={handleChange}
                    disabled={isFieldDisabled("isManualRented")}
                  />
                  Vinç Kirada
                </label>
              </div>

              {formData.isManualRented && (
                <>
                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Kiralanan Firma</label>
                    <input
                      type="text"
                      name="manualRentalCompany"
                      value={formData.manualRentalCompany}
                      onChange={handleChange}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualRentalCompany")}
                    />
                  </div>

                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Kira Ücreti</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="manualRentalFee"
                      value={formData.manualRentalFee}
                      onChange={handleManualRentalFeeChange}
                      onBlur={handleManualRentalFeeBlur}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualRentalFee")}
                    />
                  </div>

                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Kira Başlangıç Tarihi</label>
                    <input
                      type="date"
                      name="manualRentalStartDate"
                      value={formData.manualRentalStartDate}
                      onChange={handleChange}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualRentalStartDate")}
                    />
                  </div>

                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Kira Bitiş Tarihi</label>
                    <input
                      type="date"
                      name="manualRentalEndDate"
                      value={formData.manualRentalEndDate}
                      onChange={handleChange}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualRentalEndDate")}
                    />
                  </div>
                </>
              )}

              <div className="tower-crane-update-page__field tower-crane-update-page__field--checkbox">
                <label className="tower-crane-update-page__checkbox-label">
                  <input
                    type="checkbox"
                    name="isManualReserved"
                    checked={formData.isManualReserved}
                    onChange={handleChange}
                    disabled={isFieldDisabled("isManualReserved")}
                  />
                  Vinç Rezerve
                </label>
              </div>

              {formData.isManualReserved && (
                <>
                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Rezerve Edilen Firma</label>
                    <input
                      type="text"
                      name="manualReserveCompany"
                      value={formData.manualReserveCompany}
                      onChange={handleChange}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualReserveCompany")}
                    />
                  </div>

                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Rezervasyon Başlangıç Tarihi</label>
                    <input
                      type="date"
                      name="manualReserveStartDate"
                      value={formData.manualReserveStartDate}
                      onChange={handleChange}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualReserveStartDate")}
                    />
                  </div>

                  <div className="tower-crane-update-page__field">
                    <label className="tower-crane-update-page__label">Rezervasyon Bitiş Tarihi</label>
                    <input
                      type="date"
                      name="manualReserveEndDate"
                      value={formData.manualReserveEndDate}
                      onChange={handleChange}
                      className="tower-crane-update-page__input"
                      disabled={isFieldDisabled("manualReserveEndDate")}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="tower-crane-update-page__actions">
              <button
                type="submit"
                className="tower-crane-update-page__button"
                disabled={saving || !selectedCraneId}
              >
                {saving ? "Kaydediliyor..." : "GÜNCELLE"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
